import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';

const TermsOfService = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const today = new Date();
  const lastUpdated = today.toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <>
      <Helmet>
        <link rel="canonical" href="https://www.extreme-clean.com.au/terms-of-service" />
        <title>Terms of Service | Extreme Clean Australia</title>
      </Helmet>
      <div className="min-h-screen bg-white pt-32 md:pt-52 pb-20">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-snug text-[#0A0A0A] mb-4">
          Terms of Service
        </h1>
        <p className="text-gray-500 mb-12">Last updated: {lastUpdated}</p>

        <div className="prose prose-lg max-w-none text-gray-700">
          <p className="mb-8">
            These Terms of Service ("Terms") govern the use of the Extreme Clean Australia website and the provision of cleaning services by Extreme Pty Ltd (ACN 663 841 361, ABN 45 760 540 704), trading as Extreme Clean Australia ("ECA", "we", "us", "our"). By using our website or engaging our services, you agree to these Terms.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">1. Our Services</h2>
          <p className="mb-6">
            Extreme Clean Australia provides commercial and industrial cleaning services across Western Australia, including but not limited to commercial kitchen exhaust, canopy, duct and rooftop fan cleaning, commercial kitchen cleaning, oven and pizza oven cleaning, tile and grout cleaning, carpet cleaning, high-pressure cleaning, strip and seal flooring, window cleaning, office cleaning, industrial warehouse cleaning, and grease filter exchange.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">2. Quotes and Pricing</h2>
          <p className="mb-6">
            Prices for our services are provided on a per-job quote basis, as job cost depends on site size, equipment scale, access and specific requirements. No price is binding until a written quote has been issued and accepted by the customer. Quotes are valid for the period stated at the time of issue, or 30 days if no period is stated.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">3. Bookings and Scheduling</h2>
          <p className="mb-6">
            Bookings are confirmed once a quote is accepted. We will make reasonable efforts to complete work within the scheduled timeframe, but timing may be affected by site access, weather, or circumstances outside our control. We will communicate any delays as soon as we become aware of them.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">4. Cancellations and Rescheduling</h2>
          <p className="mb-6">
            We request a minimum of 48 hours' notice to cancel or reschedule a booked job. Cancellations made with less than 48 hours' notice may incur a call-out fee.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">5. Payment Terms</h2>
          <p className="mb-6">
            Payment terms vary by customer and are agreed prior to commencement of work. Standard options include payment on completion, or invoiced terms of 7, 14 or 30 days for approved commercial accounts. The applicable payment terms for your account will be confirmed at the time of quoting or booking.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">6. Site Access and Customer Responsibilities</h2>
          <p className="mb-6">
            The customer is responsible for providing safe and reasonable access to the site at the agreed time, including any required inductions, permits, or safety briefings for sites with specific compliance requirements. Delays caused by restricted access may affect scheduling and may incur additional costs.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">7. Liability and Insurance</h2>
          <p className="mb-6">
            Extreme Clean Australia holds Public and Products Liability Insurance to the value of $20,000,000, and takes all reasonable care when performing work on your premises. We are not liable for pre-existing damage, wear, or faults in equipment or infrastructure not caused by our work. Any damage caused directly by our negligence during the course of works will be addressed in accordance with our insurance policy.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">8. Compliance Work</h2>
          <p className="mb-6">
            Where our services relate to fire safety or hygiene compliance (such as kitchen exhaust, canopy, duct, rooftop fan cleaning or grease filter exchange), we perform work in line with relevant industry standards. It remains the customer's responsibility to ensure compliance documentation is retained and any regulatory reporting obligations are met on their end.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">9. Website Use</h2>
          <p className="mb-6">
            This website and its content are provided for general information about our services. We make reasonable efforts to keep information accurate and current but do not guarantee the website will be error-free or uninterrupted at all times.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">10. Changes to These Terms</h2>
          <p className="mb-6">
            We may update these Terms from time to time. Continued use of our website or services after changes are posted constitutes acceptance of the updated Terms.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">11. Governing Law</h2>
          <p className="mb-6">
            These Terms are governed by the laws of Western Australia, Australia.
          </p>

          <h2 className="font-heading font-bold text-2xl text-[#0A0A0A] mt-10 mb-4">12. Contact Us</h2>
          <div className="bg-gray-50 p-6 rounded-lg">
            <p className="font-semibold text-[#0A0A0A] mb-2">Extreme Pty Ltd (ACN 663 841 361)</p>
            <p className="mb-1">Trading as Extreme Clean Australia</p>
            <p className="mb-1">ABN 45 760 540 704</p>
            <p className="mb-1">18A/92–94 Poole Street, Welshpool WA 6106</p>
            <p className="mb-1">Phone: <a href="tel:0861875523" className="text-[#3B82F6] hover:underline">(08) 6187 5523</a></p>
            <p>Email: <a href="mailto:info.eca@extreme-clean.com.au" className="text-[#3B82F6] hover:underline">info.eca@extreme-clean.com.au</a></p>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default TermsOfService;
